import anime from 'animejs';
import { schedule } from './data/schedule';
import { donationsRenderer } from './renderers/donations-renderer';

/**
 * The clinic van that drives across the break screen.
 *
 * DORMANT ON PURPOSE — nothing calls showClinicInBreak(), and the markup it needs
 * (#clinic-container-break, #clinic-van-break) is no longer in index.html. Kept because
 * it may come back.
 *
 * The ten mp4s were deleted from the repo (97 MB for an off-air segment), so
 * clinicVideoOrder holds plain strings rather than `new URL(…, import.meta.url)` —
 * parcel resolves those at build time and fails on a missing file. Reviving this means
 * restoring the files under resources/infobar/clinic/ *and* switching the array back to
 * new URL(), or parcel will not fingerprint them and they will 404.
 *
 * The infobar has its own copy of the video mapping in flag-renderer.js. If this comes
 * back, the two have to be reconciled.
 */
export const breakClinic = {
    hideBreakDonationContainerAndShowClinicInBreak: () => {
        anime({
            targets: ['#break-donations'],
            opacity: 0,
            easing: 'linear',
            duration: 1500,
            complete: function () {
                breakClinic.showClinicInBreak();
            }
        });
    },

    hideClinicInBreakAndShowBreakDonationContainer: () => {
        anime({
            targets: ['#clinic-container-break'],
            opacity: 0,
            easing: 'linear',
            duration: 1500,
            complete: function () {
                donationsRenderer.showBreakDonationContainer();
            }
        });
    },
    moveTimeout: 20,
    clinicMoveAnimationInterval: null,
    totalClinicAnimationDuration: 13000,

    moveClinic: (clinicVan) => {
        breakClinic.clinicMoveAnimationInterval = setTimeout(function () {
            clinicVan.style.left = ((parseInt(clinicVan.style.left) || 0) + 1) + 'px';
            breakClinic.moveClinic(clinicVan);
        }, breakClinic.moveTimeout);
    },

    // Prima Colombia, poi Repubblica Democratica del Congo, poi Gaza, poi Ucraina e alla fine Italia.
    // Removed from the repo along with the infobar set — see flagRenderer.clinicVideoOrder
    // for what has to change to bring them back.
    clinicVideoOrder: [
        'resources/infobar/clinic/Break-Clinica-COL.mp4',
        'resources/infobar/clinic/Break-Clinica-COD.mp4',
        'resources/infobar/clinic/Break-Clinica-PAL.mp4',
        'resources/infobar/clinic/Break-Clinica-UKR.mp4',
        'resources/infobar/clinic/Break-Clinica-ITA.mp4',
    ],

    // Manual mapping: [runIndex, videoIdx] — video switches at the given run index (inclusive).
    // runIndex -1 = before event. Entries must be sorted ascending by runIndex.
    clinicVideoMapping: [
        [-1, 0],  // COL
        [5, 1],  // COD
        [7, 2],  // PAL
        [9, 3],  // UKR
        [12, 4],  // ITA
        [14, 0],  // COL
        [17, 1],  // COD
        [20, 2],  // PAL
        [23, 3],  // UKR
        [26, 4],  // ITA
    ],

    _resolveClinicVideo: (gameIndex) => {
        const mapping = breakClinic.clinicVideoMapping;
        let videoIdx = mapping[0][1];
        for (const [run, vid] of mapping) {
            if (gameIndex >= run) videoIdx = vid;
        }
        return videoIdx;
    },

    getCorrectClinicVideo: () => {
        const videoOrder = breakClinic.clinicVideoOrder;
        const videoIdx = breakClinic._resolveClinicVideo(schedule.currentGameIndex);
        console.log(`Video index: ${videoIdx} (${videoOrder[videoIdx]})`);
        return videoOrder[videoIdx];
    },

    showClinicInBreak: async () => {
        console.log("Showing clinic...");
        const clinicVan = document.querySelector('#clinic-van-break');
        clinicVan.style.left = '-310px';
        clinicVan.style.transform = '';

        const clinicContainer = document.querySelector('#clinic-container-break');

        const clinicVideoBg = clinicContainer.querySelector('video');
        clinicVideoBg.querySelector('source').src = breakClinic.getCorrectClinicVideo();
        clinicVideoBg.load();
        await new Promise(resolve => {
            clinicVideoBg.oncanplaythrough = resolve;
            clinicVideoBg.onerror = resolve;
            console.log("Loaded clinic video.");
        });
        console.log("Playing clinic video.");
        clinicVideoBg.play();

        clinicContainer.style.display = 'block';
        anime({
            targets: clinicContainer,
            opacity: 1,
            easing: 'linear',
            duration: 1500,

            complete: function () {
                setTimeout(function () {
                    breakClinic.hideClinicInBreakAndShowBreakDonationContainer();
                }, breakClinic.totalClinicAnimationDuration);

                setTimeout(function () {
                    breakClinic.moveTimeout = 18;
                    breakClinic.moveClinic(clinicVan);
                }, 600);
                anime({
                    delay: 600,
                    targets: clinicVan,
                    translateX: [
                        { value: 0, duration: 0 },
                        { value: 600, duration: 700 },
                    ],
                    rotate: [
                        { value: 0, duration: 100 },
                        { value: 5, duration: 600 },
                        { value: 0, duration: 200 },
                    ],
                    easing: 'easeOutCubic',
                    complete: function () {
                        const bobbing = anime({
                            targets: clinicVan,
                            loop: true,
                            scaleX: [
                                { value: 0.98, duration: 350, easing: 'easeInOutCubic' },
                                { value: 1.02, duration: 350, easing: 'easeInOutCubic' },
                            ],
                            scaleY: [
                                { value: 1.01, duration: 300, easing: 'easeInOutCubic' },
                                { value: 0.99, duration: 300, easing: 'easeInOutCubic' },
                            ],
                        });
                        setTimeout(function () {
                            bobbing.pause();

                            console.log("Stopping clinic move animation in break...", breakClinic.clinicMoveAnimationInterval);
                            clearInterval(breakClinic.clinicMoveAnimationInterval);
                            breakClinic.clinicMoveAnimationInterval = null;
                            console.log("Hiding clinic...");
                            clinicVan.style.left = '-310px';
                            clinicVan.transform = '';
                        }, breakClinic.totalClinicAnimationDuration + 1000);
                    }
                });
            }
        });

    },
};

window.breakClinic = breakClinic;

/**
 * The infobar's own clinic segment — a second van, a second set of videos, the same
 * run-index → video mapping. That mapping used to be copied into flag-renderer.js, with
 * a note in CLAUDE.md to keep the two in sync; both copies now read
 * breakClinic.clinicVideoMapping.
 *
 * DORMANT, like the break one. `debugClinicVideoMapping()` prints the resolved mapping
 * against the real schedule and is safe to call from the console.
 */
export const infobarClinic = {
    moveTimeout: 20,
    clinicMoveAnimationInterval: null,


    moveClinic: (clinicVan) => {
        infobarClinic.clinicMoveAnimationInterval = setTimeout(function () {
            clinicVan.style.left = ((parseInt(clinicVan.style.left) || 0) + 1) + 'px';
            infobarClinic.moveClinic(clinicVan);
        }, infobarClinic.moveTimeout);
    },

    // Prima Colombia, poi Repubblica Democratica del Congo, poi Gaza, poi Ucraina e alla fine Italia.
    //
    // The files themselves are no longer in the repo: 97 MB of mp4 for a segment that is
    // not on air. Plain strings on purpose — `new URL(…, import.meta.url)` is a build-time
    // dependency and parcel fails the whole build when the target is missing. Putting the
    // videos back means restoring them under this path *and* switching these back to
    // `new URL()` so parcel fingerprints and rewrites them.
    clinicVideoOrder: [
        'resources/infobar/clinic/Infobar-Clinica-COL.mp4',
        'resources/infobar/clinic/Infobar-Clinica-COD.mp4',
        'resources/infobar/clinic/Infobar-Clinica-PAL.mp4',
        'resources/infobar/clinic/Infobar-Clinica-UKR.mp4',
        'resources/infobar/clinic/Infobar-Clinica-ITA.mp4',
    ],

    getCorrectClinicVideo: () => {
        const videoOrder = infobarClinic.clinicVideoOrder;
        const videoIdx = breakClinic._resolveClinicVideo(schedule.currentGameIndex);
        console.log(`Video index: ${videoIdx} (${videoOrder[videoIdx]})`);
        return videoOrder[videoIdx];
    },

    debugClinicVideoMapping: () => {
        const videoOrder = infobarClinic.clinicVideoOrder;
        const rows = [];
        for (let i = -1; i <= schedule.schedule.length - 1; i++) {
            const videoIdx = breakClinic._resolveClinicVideo(i);
            const gameName = i === -1 ? '(before event)' : (schedule.schedule[i]?.name ?? '(unknown)');
            rows.push({ index: i, game: gameName, video: videoOrder[videoIdx].split('/').pop() });
        }
        console.table(rows);
    },

    showClinic: async () => {
        console.log("Showing clinic...");
        const clinicVan = document.querySelector('#clinic-van');
        clinicVan.style.left = '110px';
        clinicVan.style.transform = '';

        const clinicContainer = document.querySelector('#clinic-container');

        const clinicVideoBg = clinicContainer.querySelector('video');
        clinicVideoBg.querySelector('source').src = infobarClinic.getCorrectClinicVideo();
        clinicVideoBg.load();
        await new Promise(resolve => {
            clinicVideoBg.oncanplaythrough = resolve;
            clinicVideoBg.onerror = resolve;
        });
        clinicVideoBg.play();

        clinicContainer.style.display = 'block';
        const totalAnimationDuration = 15000;
        anime({
            targets: clinicContainer,
            opacity: 1,
            easing: 'linear',
            duration: 200,

            complete: function () {
                setTimeout(function () {
                    infobarClinic.moveTimeout = 20;
                    infobarClinic.moveClinic(clinicVan);
                }, 600);
                anime({
                    targets: clinicVan,
                    translateX: [
                        { value: 0, duration: 10 },
                        { value: 300, duration: 700 },
                    ],
                    rotate: [
                        { value: 0, duration: 100 },
                        { value: 4, duration: 600 },
                        { value: 0, duration: 200 },
                    ],
                    easing: 'easeOutCubic',
                    complete: function () {
                        const bobbing = anime({
                            targets: clinicVan,
                            loop: true,
                            scaleX: [
                                { value: 0.98, duration: 250, easing: 'easeInOutCubic' },
                                { value: 1.02, duration: 250, easing: 'easeInOutCubic' },
                            ],
                            scaleY: [
                                { value: 1.01, duration: 200, easing: 'easeInOutCubic' },
                                { value: 0.99, duration: 200, easing: 'easeInOutCubic' },
                            ],
                        });
                        setTimeout(function () {
                            bobbing.pause();

                            console.log("Stopping clinic move animation...", infobarClinic.clinicMoveAnimationInterval);
                            clearInterval(infobarClinic.clinicMoveAnimationInterval);
                            infobarClinic.clinicMoveAnimationInterval = null;
                        }, totalAnimationDuration + 1000);
                        anime({
                            delay: totalAnimationDuration,
                            targets: clinicContainer,
                            opacity: 0,
                            easing: 'linear',
                            duration: 200,
                            complete: function () {
                                console.log("Hiding clinic...");
                                clinicVan.style.left = '110px';
                                clinicVan.transform = '';
                                setTimeout(function () {
                                    inforoll.start();
                                }, 1000);
                            }
                        });
                    }
                });
            }
        });

    },

};

window.infobarClinic = infobarClinic;
